export type ColorScale = Record<number, string>;

/**
 * Shades explicitly specified by the Elementa design system. The semantic
 * feedback ramps (success/warning/error/info) only define a subset; the gaps
 * are filled by HSL derivation from a stable anchor so every ramp exposes the
 * full 25-950 range.
 */
const referenceScales = {
  primary: {
    25: '#f3faf2', 50: '#e8f5e6', 100: '#c6e6c3', 200: '#a0d49b',
    300: '#78c170', 400: '#58b24e', 500: '#38a229', 600: '#2d9221',
    700: '#1f7e17', 800: '#1a5c16', 900: '#11420f', 950: '#0a2e08',
  },
  secondary: {
    25: '#fafafa', 50: '#f5f5f5', 100: '#f3f3f3', 200: '#e5e5e5',
    300: '#d4d4d4', 400: '#a3a3a3', 500: '#767676', 600: '#595959',
    700: '#454545', 800: '#373737', 900: '#262626', 950: '#171717',
  },
  neutral: {
    0: '#ffffff', 25: '#fcfcfc', 50: '#fafafa', 100: '#f3f3f3',
    200: '#e5e5e5', 300: '#d4d4d4', 400: '#a3a3a3', 500: '#767676',
    600: '#595959', 700: '#454545', 800: '#373737', 900: '#262626',
    950: '#171717',
  },
  accent: {
    25: '#edfce9', 50: '#d6f9d0', 100: '#aef2a3', 200: '#7de86d',
    300: '#52d940', 400: '#30c41e', 500: '#16a34a', 600: '#007500',
    700: '#006200', 800: '#004f00', 900: '#003d00', 950: '#002800',
  },
  success: {
    50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 500: '#10b981',
    600: '#059669', 700: '#047857',
  },
  warning: {
    50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 500: '#f59e0b',
    600: '#d97706', 700: '#b45309',
  },
  error: {
    50: '#fef2f2', 100: '#fee2e2', 200: '#fecaca', 500: '#ef4444',
    600: '#dc2626', 700: '#b91c1c', 800: '#991b1b',
  },
  info: {
    50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 500: '#3b82f6',
    600: '#2563eb', 700: '#1d4ed8',
  },
  magic: {
    25: '#fbf5ff', 50: '#f6e9fd', 100: '#eddcfa', 200: '#d8b4f8',
    300: '#c084fc', 400: '#aa51d9', 500: '#9333ea', 600: '#7e22ce',
    700: '#6d28d9', 800: '#581c87', 900: '#3b0764', 950: '#3b0764',
  },
} satisfies Record<string, ColorScale>;

const shadeKeys = [25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

/** Lightness delta applied to the anchor for each shade; negative values darken. */
const shadeLightnessDeltas: Record<number, number> = {
  25: 0.95, 50: 0.88, 100: 0.78, 200: 0.65,
  300: 0.5, 400: 0.35, 500: 0.2, 600: 0.1,
  700: 0.05, 800: 0, 900: -0.08, 950: -0.15,
};

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.replace(/^#/, ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHsl(red: number, green: number, blue: number): [number, number, number] {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    default:
      h = ((r - g) / d + 4) / 6;
      break;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const s = saturation / 100;
  const l = lightness / 100;
  const a = s * Math.min(l, 1 - l);
  const channel = (n: number) => {
    const k = (n + hue / 30) % 12;
    return Math.round(255 * (l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)));
  };
  return '#' + [channel(0), channel(8), channel(4)].map((x) => x.toString(16).padStart(2, '0')).join('');
}

function shiftLightness(hex: string, amount: number): string {
  const [h, s, l] = rgbToHsl(...hexToRgb(hex));
  const next = amount >= 0 ? Math.min(100, l + amount * 100) : Math.max(0, l + amount * 100);
  return hslToHex(h, s, next);
}

/** Build a full 25-950 ramp from a single anchor hex via HSL lighten/darken. */
export function deriveColorScale(anchorHex: string): ColorScale {
  const scale: ColorScale = {};
  for (const key of shadeKeys) {
    scale[key] = shiftLightness(anchorHex, shadeLightnessDeltas[key]);
  }
  return scale;
}

/** Design-system shades win; derived shades only fill the gaps. */
function fullScaleFromPartial(anchorHex: string, partial: ColorScale): ColorScale {
  return { ...deriveColorScale(anchorHex), ...partial };
}

export const paletteScales = {
  primary: referenceScales.primary as ColorScale,
  secondary: referenceScales.secondary as ColorScale,
  neutral: referenceScales.neutral as ColorScale,
  accent: referenceScales.accent as ColorScale,
  success: fullScaleFromPartial('#059669', referenceScales.success),
  warning: fullScaleFromPartial('#d97706', referenceScales.warning),
  error: fullScaleFromPartial('#dc2626', referenceScales.error),
  info: fullScaleFromPartial('#2563eb', referenceScales.info),
  magic: referenceScales.magic as ColorScale,
} satisfies Record<string, ColorScale>;

export const capioAppearance = {
  primary: paletteScales.primary[800],
  secondary: paletteScales.secondary[500],
  positive: paletteScales.success[600],
  warning: paletteScales.warning[600],
  negative: paletteScales.error[600],
  magic: paletteScales.magic[500],
  backgroundColour: paletteScales.neutral[50],
} as const;

export function hexToRgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}
